import React, { useState, useEffect } from "react";
import { usePermissions } from "../context/PermissionContext";
import { useParams, useNavigate } from "react-router-dom";
import { CostCategory, CalculationType, Stock } from "../types";
import { costService } from "../services/dataService";
import { getStocks } from "../services/api";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import "./PurchaseOrders.css";
import "./CostItemForm.css";

interface CostItemFormProps {}

// Hesaplama tipi açıklamaları
const calculationTypeLabels: Record<CalculationType, string> = {
  [CalculationType.Simple]: "Basit (Miktar × Birim Fiyat)",
  [CalculationType.TwoDimensional]:
    "İki Boyutlu (Miktar × Miktar2 × Birim Fiyat)",
  [CalculationType.PieceFitting]: "Varak/Sticker Sığdırma",
  [CalculationType.MeterBased]: "Metre Bazlı",
  [CalculationType.AreaBased]: "Alan Bazlı (m²)",
  [CalculationType.PaintBased]: "Boya Hesaplama",
  [CalculationType.CustomCost]: "Özel Maliyet (Fason/Sabit Tutar)",
};

// Hesaplama tipine göre parametre sayısı ve varsayılan başlıklar
// Not: Sipariş adedi otomatik olarak siparişten alınır, bu yüzden parametre olarak girilmez
const calculationTypeConfig: Record<
  CalculationType,
  {
    paramCount: number;
    defaultLabels: string[];
    description: string;
    needsMaterialDimensions?: boolean;
    usesOrderQuantity?: boolean; // Sipariş adedini otomatik kullanır mı?
  }
> = {
  [CalculationType.Simple]: {
    paramCount: 1,
    defaultLabels: ["Miktar"],
    description: "Basit hesaplama: Miktar × Birim Fiyat",
  },
  [CalculationType.TwoDimensional]: {
    paramCount: 2,
    defaultLabels: ["En", "Boy"],
    description:
      "Çift boyutlu: Miktar1 × Miktar2 × Birim Fiyat (örn: en × boy)",
  },
  [CalculationType.PieceFitting]: {
    paramCount: 2,
    defaultLabels: ["En (cm)", "Boy (cm)"],
    description:
      "Varak/Sticker sığdırma: Parça boyutlarına göre malzemeye kaç adet sığdığını hesaplar. Sipariş adedi otomatik alınır.",
    needsMaterialDimensions: true,
    usesOrderQuantity: true,
  },
  [CalculationType.MeterBased]: {
    paramCount: 1,
    defaultLabels: ["Metre"],
    description:
      "Metre bazlı: Metre × Sipariş Adedi şeklinde hesaplama yapar. Sipariş adedi otomatik alınır.",
    usesOrderQuantity: true,
  },
  [CalculationType.AreaBased]: {
    paramCount: 2,
    defaultLabels: ["En (m)", "Boy (m)"],
    description:
      "Alan bazlı: En × Boy × Sipariş Adedi (m² cinsinden) hesaplama yapar. Sipariş adedi otomatik alınır.",
    usesOrderQuantity: true,
  },
  [CalculationType.PaintBased]: {
    paramCount: 2,
    defaultLabels: ["Litre", "Çeşit Sayısı"],
    description:
      "Boya hesaplama: Litre × Fire. Çeşit sayısı yalnız bilgi amaçlıdır, hesaplamaya katılmaz.",
  },
  [CalculationType.CustomCost]: {
    paramCount: 0,
    defaultLabels: [],
    description:
      "Özel maliyet: TotalCost değeri direkt girilir. Birim fiyat, miktar ve fire hesaplaması yapılmaz. Fason üretim, paket hizmetler ve sabit maliyetler için kullanılır.",
  },
};

const svg = (d: string) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={d} />
  </svg>
);
const I = {
  plus: svg("M12 5v14M5 12h14"),
  x: svg("M18 6 6 18M6 6l12 12"),
  trash: svg("M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v6M14 11v6"),
};

const CostItemForm: React.FC<CostItemFormProps> = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEditing = Boolean(id && id !== "new");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState<CostCategory[]>([]);
  const [stocks, setStocks] = useState<Stock[]>([]);
  // Kaydet'e basıldıktan sonra alan altı hata metinlerini göster
  const [tried, setTried] = useState(false);

  const { hasModule } = usePermissions();
  const [formData, setFormData] = useState({
    itemName: "",
    unitPrice: 0,
    currency: "TRY",
    costCategoryId: "",
    // Bu kalemin tükettiği stoklar (0..N) — maliyet girildiği anda düşer
    stocks: [] as { stockId: string; quantity: number; wastagePercentage: number }[],
    supplier: "",
    wastagePercentage: 0, // Fire yüzdesi (0-100)
    isActive: true,
    // Yeni hesaplama tipi alanları
    calculationType: CalculationType.Simple,
    materialWidth: 0, // Malzeme eni (Varak: 150, Sticker: 59)
    materialHeight: 0, // Malzeme boyu per metre (100)
    quantity1Label: "", // 1. miktar başlığı
    quantity2Label: "", // 2. miktar başlığı
    quantity3Label: "", // 3. miktar başlığı
  });

  // Alert Modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  // Confirm Modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "warning" | "danger";
    onConfirm: () => void;
  }>({ isOpen: false, message: "", type: "warning", onConfirm: () => {} });

  useEffect(() => {
    loadCategories();
    loadStocks();
    if (isEditing && id) {
      loadCostItem(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditing]);

  // ---- Stok bağı satırları ----
  const addStockRow = () =>
    setFormData((f) => ({
      ...f,
      stocks: [...(f.stocks ?? []), { stockId: "", quantity: 1, wastagePercentage: 0 }],
    }));
  const patchStockRow = (
    i: number,
    patch: Partial<{ stockId: string; quantity: number; wastagePercentage: number }>,
  ) =>
    setFormData((f) => ({
      ...f,
      stocks: (f.stocks ?? []).map((r, ix) => (ix === i ? { ...r, ...patch } : r)),
    }));
  const removeStockRow = (i: number) =>
    setFormData((f) => ({ ...f, stocks: (f.stocks ?? []).filter((_, ix) => ix !== i) }));

  const loadStocks = async () => {
    try {
      const res = await getStocks();
      setStocks((res.data || []).filter((s: Stock) => s.isActive !== false));
    } catch {
      // Stok modülü kapalı/erişilemez olabilir — bağlantı alanı boş listeyle kalır
    }
  };

  const loadCategories = async () => {
    try {
      const data = await costService.getCostCategories();
      setCategories(data);
    } catch (error) {
      console.error("❌ Failed to load categories:", error);
      setAlertModal({
        isOpen: true,
        message: "Kategoriler yüklenemedi!",
        type: "error",
      });
    }
  };

  const loadCostItem = async (itemId: string) => {
    try {
      setLoading(true);
      const data = await costService.getCostItemById(itemId);

      setFormData({
        itemName: data.itemName,
        unitPrice: data.unitPrice,
        currency: data.currency,
        costCategoryId: data.costCategoryId,
        stocks: (data.stocks ?? []).map((s: any) => ({ stockId: s.stockId, quantity: s.quantity ?? 1, wastagePercentage: s.wastagePercentage ?? 0 })),
        supplier: data.supplier || "",
        wastagePercentage: data.wastagePercentage || 0,
        isActive: data.isActive,
        // Yeni hesaplama tipi alanları
        calculationType: data.calculationType ?? CalculationType.Simple,
        materialWidth: data.materialWidth || 0,
        materialHeight: data.materialHeight || 0,
        quantity1Label: data.quantity1Label || "",
        quantity2Label: data.quantity2Label || "",
        quantity3Label: data.quantity3Label || "",
      });
    } catch (error) {
      console.error("❌ Failed to load cost item:", error);
      setAlertModal({
        isOpen: true,
        message: "Maliyet kalemi yüklenemedi!",
        type: "error",
      });
      navigate("/cost");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setTried(true);
    if (!formData.itemName.trim()) {
      setAlertModal({
        isOpen: true,
        message: "Kalem adı zorunludur!",
        type: "warning",
      });
      return;
    }

    if (!formData.costCategoryId) {
      setAlertModal({
        isOpen: true,
        message: "Kategori seçimi zorunludur!",
        type: "warning",
      });
      return;
    }

    // CustomCost tipinde birim fiyat kontrolü yapma
    if (
      formData.calculationType !== CalculationType.CustomCost &&
      formData.unitPrice <= 0
    ) {
      setAlertModal({
        isOpen: true,
        message: "Birim fiyat sıfırdan büyük olmalıdır!",
        type: "warning",
      });
      return;
    }

    try {
      setSaving(true);

      const itemData = {
        ...formData,
        wastagePercentage: formData.wastagePercentage || 0, // Fire yüdesi
        // Stoğu seçilmemiş satırlar gönderilmez
        stocks: (formData.stocks ?? []).filter((s) => s.stockId),
        supplier: formData.supplier || undefined,
        // Hesaplama tipi alanları
        calculationType: formData.calculationType,
        materialWidth: formData.materialWidth || undefined,
        materialHeight: formData.materialHeight || undefined,
        quantity1Label: formData.quantity1Label || undefined,
        quantity2Label: formData.quantity2Label || undefined,
        quantity3Label: formData.quantity3Label || undefined,
      };

      if (isEditing && id) {
        // Backend'de full update endpoint'i yok - sadece fiyat güncellenebilir
        setAlertModal({
          isOpen: true,
          message:
            "Maliyet kalemi düzenleme şu anda desteklenmiyor.\n\nSadece fiyat güncellemesi yapabilirsiniz veya yeni bir kayıt oluşturabilirsiniz.",
          type: "warning",
        });
        navigate("/cost");
        return;
      } else {
        // Create new item
        await costService.createCostItem(itemData);
        setAlertModal({
          isOpen: true,
          message: "Maliyet kalemi başarıyla oluşturuldu!",
          type: "success",
        });
      }

      navigate("/cost");
    } catch (error) {
      console.error("❌ Failed to save cost item:", error);
      setAlertModal({
        isOpen: true,
        message: "Maliyet kalemi kaydedilemedi!",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEditing || !id) return;

    setConfirmModal({
      isOpen: true,
      message: "Bu maliyet kalemini silmek istediğinizden emin misiniz?",
      type: "danger",
      onConfirm: async () => {
        try {
          await costService.deleteCostItem(id);
          setAlertModal({
            isOpen: true,
            message: "Maliyet kalemi başarıyla silindi!",
            type: "success",
          });
          navigate("/cost");
        } catch (error) {
          console.error("❌ Failed to delete cost item:", error);
          setAlertModal({
            isOpen: true,
            message:
              "Maliyet kalemi silinemedi! Bu kaleme bağlı model maliyetleri olabilir.",
            type: "error",
          });
        }
      },
    });
  };

  const currencies = ["TRY", "USD", "EUR"];

  const cfg = calculationTypeConfig[formData.calculationType];
  const isCustom = formData.calculationType === CalculationType.CustomCost;
  const stockRows = formData.stocks ?? [];
  const nameErr = tried && !formData.itemName.trim();
  const catErr = tried && !formData.costCategoryId;
  const priceErr = tried && !isCustom && formData.unitPrice <= 0;
  const blockArrows = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
    }
  };

  const saveBtn = (
    <button
      type="button"
      className="po-btn primary"
      onClick={handleSave}
      disabled={saving || loading}
    >
      {saving ? "Kaydediliyor..." : "Kaydet"}
    </button>
  );

  return (
    <div className="po-page ci-page">
      <header className="po-head">
        <div>
          <button type="button" className="po-link ci-back" onClick={() => navigate("/cost")}>
            ← Maliyet Yönetimi
          </button>
          <span className="po-kicker">Maliyet</span>
          <h1>{isEditing ? "Maliyet Kalemini Düzenle" : "Yeni Maliyet Kalemi"}</h1>
          <p>
            Kalemin nasıl hesaplanacağını, fiyatını ve varsa düştüğü stokları tanımlayın.
          </p>
        </div>
        <div className="po-head-actions">
          {isEditing && (
            <button type="button" className="po-btn danger" onClick={handleDelete}>
              {I.trash}
              Sil
            </button>
          )}
          {saveBtn}
        </div>
      </header>

      {isEditing && !loading && (
        <div className="po-note warn">
          Maliyet kalemi düzenleme şu anda desteklenmiyor: yalnız fiyat güncellemesi
          yapılabilir ya da yeni bir kayıt oluşturulabilir.
        </div>
      )}

      {loading ? (
        <div className="po-card">
          <div className="po-loading">
            <span className="po-spin" /> Yükleniyor...
          </div>
        </div>
      ) : (
        <>
          <div className={`ci-layout${saving ? " dim" : ""}`}>
            <div className="ci-col">
              {/* Temel bilgiler */}
              <section className="po-card">
                <div className="ci-body">
                  <div className="po-form-sec">
                    <h4>Temel bilgiler</h4>
                    <div className="po-grid2">
                      <label className="po-field">
                        <span>
                          Kalem adı<em className={nameErr ? "err" : ""}>zorunlu</em>
                        </span>
                        <input
                          type="text"
                          value={formData.itemName}
                          onChange={(e) =>
                            setFormData({ ...formData, itemName: e.target.value })
                          }
                          placeholder="Örn: Pamuk İplik, Polyester Kumaş"
                          className={nameErr ? "is-invalid" : undefined}
                          aria-invalid={nameErr || undefined}
                          required
                        />
                        {nameErr && <span className="po-fielderr">Kalem adı zorunludur.</span>}
                      </label>
                      <label className="po-field">
                        <span>
                          Kategori<em className={catErr ? "err" : ""}>zorunlu</em>
                        </span>
                        <select
                          value={formData.costCategoryId}
                          onChange={(e) =>
                            setFormData({ ...formData, costCategoryId: e.target.value })
                          }
                          className={catErr ? "is-invalid" : undefined}
                          aria-invalid={catErr || undefined}
                          required
                        >
                          <option value="">Kategori seçiniz</option>
                          {categories.map((category) => (
                            <option
                              key={category.costCategoryId}
                              value={category.costCategoryId}
                            >
                              {category.categoryName}
                            </option>
                          ))}
                        </select>
                        {catErr && <span className="po-fielderr">Kategori seçimi zorunludur.</span>}
                      </label>
                    </div>
                  </div>
                </div>
              </section>

              {/* Hesaplama */}
              <section className="po-card">
                <div className="ci-body">
                  <div className="po-form-sec">
                    <h4>Hesaplama</h4>
                    <label className="po-field">
                      <span>
                        Hesaplama tipi<em>zorunlu</em>
                      </span>
                      <select
                        value={formData.calculationType}
                        onChange={(e) => {
                          const newType = parseInt(e.target.value) as CalculationType;
                          const config = calculationTypeConfig[newType];
                          // Hesaplama tipi değiştiğinde varsayılan başlıkları ayarla
                          setFormData({
                            ...formData,
                            calculationType: newType,
                            quantity1Label: config.defaultLabels[0] || "",
                            quantity2Label: config.defaultLabels[1] || "",
                            quantity3Label: config.defaultLabels[2] || "",
                          });
                        }}
                      >
                        {Object.entries(calculationTypeLabels).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                      <span className="po-fieldnote">{cfg?.description}</span>
                    </label>

                    {/* Varak/Sticker için malzeme boyutları */}
                    {cfg?.needsMaterialDimensions && (
                      <div className="po-grid2">
                        <label className="po-field">
                          <span>
                            Malzeme eni<em>zorunlu</em>
                          </span>
                          <div className="po-money">
                            <input
                              type="number"
                              value={formData.materialWidth || ""}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  materialWidth: parseFloat(e.target.value) || 0,
                                })
                              }
                              placeholder="Örn: 150 (Varak), 59 (Sticker)"
                              min="0"
                              step="0.1"
                            />
                            <i>cm</i>
                          </div>
                          <span className="po-fieldnote">Malzeme rulo/levha eni</span>
                        </label>
                        <label className="po-field">
                          <span>
                            Malzeme boyu (metre başına)<em>zorunlu</em>
                          </span>
                          <div className="po-money">
                            <input
                              type="number"
                              value={formData.materialHeight || ""}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  materialHeight: parseFloat(e.target.value) || 0,
                                })
                              }
                              placeholder="Örn: 100"
                              min="0"
                              step="0.1"
                            />
                            <i>cm</i>
                          </div>
                          <span className="po-fieldnote">1 metre malzemede kullanılabilir boy</span>
                        </label>
                      </div>
                    )}

                    {/* Dinamik parametre başlıkları */}
                    <div className="ci-params">
                      <div className="ci-params-head">
                        <b>Miktar giriş alanları</b>
                        <span className="po-badge info">{cfg?.paramCount} parametre</span>
                      </div>
                      <div className="ci-params-grid">
                        {/* 1. Parametre - her zaman göster */}
                        <label className="po-field ci-param">
                          <span>
                            <i className="ci-num">1</i>Parametre başlığı
                          </span>
                          <input
                            type="text"
                            value={formData.quantity1Label}
                            onChange={(e) =>
                              setFormData({ ...formData, quantity1Label: e.target.value })
                            }
                            placeholder={cfg?.defaultLabels[0] || "Miktar"}
                          />
                        </label>
                        {/* 2. Parametre - paramCount >= 2 ise göster */}
                        {cfg?.paramCount >= 2 && (
                          <label className="po-field ci-param">
                            <span>
                              <i className="ci-num">2</i>Parametre başlığı
                            </span>
                            <input
                              type="text"
                              value={formData.quantity2Label}
                              onChange={(e) =>
                                setFormData({ ...formData, quantity2Label: e.target.value })
                              }
                              placeholder={cfg?.defaultLabels[1] || "Miktar 2"}
                            />
                          </label>
                        )}
                        {/* 3. Parametre - paramCount >= 3 ise göster */}
                        {cfg?.paramCount >= 3 && (
                          <label className="po-field ci-param">
                            <span>
                              <i className="ci-num">3</i>Parametre başlığı
                            </span>
                            <input
                              type="text"
                              value={formData.quantity3Label}
                              onChange={(e) =>
                                setFormData({ ...formData, quantity3Label: e.target.value })
                              }
                              placeholder={cfg?.defaultLabels[2] || "Miktar 3"}
                            />
                          </label>
                        )}
                      </div>
                      <span className="po-fieldnote">
                        Bu başlıklar transfer modalında miktar girişlerinde görünecektir.
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              {/* Stok düşümü */}
              {hasModule("stock") && (
                <section className="po-card">
                  <div className="ci-body">
                    <div className="po-form-sec">
                      <h4>
                        Stok düşümü <em>{stockRows.length}</em>
                        <button type="button" className="po-btn small" onClick={addStockRow}>
                          {I.plus} Stok ekle
                        </button>
                      </h4>
                      <p className="po-form-hint">
                        Bu kalem bir sipariş maliyetine girildiği <b>anda</b> aşağıdaki stoklardan düşer;
                        maliyet satırı düzenlenirse fark düzeltilir, silinirse stok geri konur.
                        Miktar, kalemin <b>bir birimi</b> içindir: 0,2 kg boya yazarsan 30 birimlik
                        maliyette 6 kg düşer. Seçtiğin stoğun ürün ağacında reçetesi varsa düşüm
                        kendisinden değil bileşenlerinden yapılır.
                      </p>

                      {stockRows.length === 0 ? (
                        <div className="po-blank small">
                          <b>Bağlı stok yok</b>
                          Bu kalem stoğa dokunmaz.
                        </div>
                      ) : (
                        <div className="po-table-wrap tight">
                          <table className="po-table small ci-stocks">
                            <thead>
                              <tr>
                                <th className="ci-no">#</th>
                                <th>Stok</th>
                                <th className="num">Miktar (1 birim için)</th>
                                <th className="num">Fire (%)</th>
                                <th className="act" aria-label="İşlem" />
                              </tr>
                            </thead>
                            <tbody>
                              {stockRows.map((row, i) => {
                                const picked = stocks.find((s) => s.id === row.stockId);
                                return (
                                  <tr className="static" key={i}>
                                    <td className="ci-no">{i + 1}</td>
                                    <td className="ci-stockcell">
                                      <select
                                        className="po-select"
                                        aria-label={`${i + 1}. stok`}
                                        value={row.stockId}
                                        onChange={(e) => patchStockRow(i, { stockId: e.target.value })}
                                      >
                                        <option value="">Seçiniz…</option>
                                        {stocks.map((s) => (
                                          <option key={s.id} value={s.id}>
                                            {s.productCode ? `${s.productCode} — ` : ""}{s.productName} ({s.unit})
                                          </option>
                                        ))}
                                      </select>
                                      {picked && (
                                        <span className="po-fieldnote">
                                          Mevcut bakiye: {picked.currentStock} {picked.unit}
                                        </span>
                                      )}
                                    </td>
                                    <td className="num">
                                      <input
                                        className="ci-cell"
                                        type="number"
                                        step="0.0001"
                                        aria-label={`${i + 1}. stok miktarı (1 birim için)`}
                                        value={row.quantity}
                                        onChange={(e) => patchStockRow(i, { quantity: Number(e.target.value) })}
                                      />
                                    </td>
                                    <td className="num">
                                      <input
                                        className="ci-cell"
                                        type="number"
                                        step="0.01"
                                        aria-label={`${i + 1}. stok fire yüzdesi`}
                                        value={row.wastagePercentage}
                                        onChange={(e) => patchStockRow(i, { wastagePercentage: Number(e.target.value) })}
                                      />
                                    </td>
                                    <td className="act">
                                      <button
                                        type="button"
                                        className="po-ib danger"
                                        aria-label={`${i + 1}. stok satırını kaldır`}
                                        title="Kaldır"
                                        onClick={() => removeStockRow(i)}
                                      >
                                        {I.x}
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              )}
            </div>

            <div className="ci-col ci-side">
              {/* Fiyat */}
              <section className="po-card">
                <div className="ci-body">
                  <div className="po-form-sec">
                    <h4>Fiyat</h4>
                    {isCustom ? (
                      <div className="po-note warn">
                        <b>Özel maliyet modu.</b> Bu hesaplama tipinde <b>birim fiyat kullanılmaz</b>.
                        Toplam maliyet tutarı transfer modalında direkt girilecektir. Fason üretim,
                        paket hizmetler ve sabit maliyetler için uygundur.
                      </div>
                    ) : (
                      <label className="po-field">
                        <span>
                          Birim fiyat<em className={priceErr ? "err" : ""}>zorunlu</em>
                        </span>
                        <div className="po-money">
                          <input
                            type="number"
                            value={formData.unitPrice}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                unitPrice: parseFloat(e.target.value) || 0,
                              })
                            }
                            onKeyDown={blockArrows}
                            placeholder="0.00"
                            min="0"
                            step="0.01"
                            className={priceErr ? "is-invalid" : undefined}
                            aria-invalid={priceErr || undefined}
                            required
                          />
                          <i>{formData.currency}</i>
                        </div>
                        {priceErr && (
                          <span className="po-fielderr">Birim fiyat sıfırdan büyük olmalıdır.</span>
                        )}
                      </label>
                    )}

                    <div className="po-field">
                      <span>Döviz</span>
                      <div className="po-seg" role="radiogroup" aria-label="Döviz">
                        {currencies.map((currency) => (
                          <button
                            key={currency}
                            type="button"
                            role="radio"
                            aria-checked={formData.currency === currency}
                            className={formData.currency === currency ? "on" : ""}
                            onClick={() => setFormData({ ...formData, currency })}
                          >
                            {currency}
                          </button>
                        ))}
                      </div>
                    </div>

                    {!isCustom && (
                      <label className="po-field">
                        <span>
                          Fire yüzdesi<em>isteğe bağlı</em>
                        </span>
                        <div className="po-money">
                          <input
                            type="number"
                            value={formData.wastagePercentage}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                wastagePercentage: parseFloat(e.target.value) || 0,
                              })
                            }
                            onKeyDown={blockArrows}
                            placeholder="Örn: 10 (%10 fire)"
                            min="0"
                            max="100"
                            step="0.1"
                          />
                          <i>%</i>
                        </div>
                      </label>
                    )}
                  </div>
                </div>
              </section>

              {/* Tedarikçi ve durum */}
              <section className="po-card">
                <div className="ci-body">
                  <div className="po-form-sec">
                    <h4>Tedarikçi ve durum</h4>
                    <label className="po-field">
                      <span>
                        Tedarikçi<em>isteğe bağlı</em>
                      </span>
                      <input
                        type="text"
                        value={formData.supplier}
                        onChange={(e) =>
                          setFormData({ ...formData, supplier: e.target.value })
                        }
                        placeholder="Tedarikçi firma adı"
                      />
                    </label>
                    <label className="po-check">
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) =>
                          setFormData({ ...formData, isActive: e.target.checked })
                        }
                      />
                      Aktif
                    </label>
                  </div>
                </div>
              </section>
            </div>
          </div>

          <div className="ci-actions">{saveBtn}</div>
        </>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={() => {
          confirmModal.onConfirm();
          setConfirmModal({ ...confirmModal, isOpen: false });
        }}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </div>
  );
};

export default CostItemForm;
